/**
 * The world rectangle a camera will draw this frame, worked out from its scroll and zoom as the scene's update left
 * them. Phaser writes a camera's `worldView` only as it renders, after every scene's update, so during an update
 * `worldView` is still the rectangle the frame before drew; what is loaded for the frame about to be drawn has to be
 * decided from this rectangle. The sums are those of Phaser's `Camera.preRender` for a camera that follows nothing:
 * the scroll clamped to the camera's bounds, then the view centred on the scrolled viewport's middle at the size the
 * zoom leaves.
 */

/**
 * The part of a Phaser camera the rectangle is worked out from.
 */
export interface ScrolledCamera {
  readonly scrollX: number;
  readonly scrollY: number;
  readonly width: number;
  readonly height: number;
  readonly zoomX: number;
  readonly zoomY: number;
  readonly useBounds: boolean;
  /**
   * Clamps a horizontal scroll to the camera's bounds.
   *
   * @param x The scroll.
   * @returns The clamped scroll.
   */
  clampX(x: number): number;
  /**
   * Clamps a vertical scroll to the camera's bounds.
   *
   * @param y The scroll.
   * @returns The clamped scroll.
   */
  clampY(y: number): number;
}

/**
 * A view rectangle in world pixels that a caller keeps and has filled in place.
 */
export interface ViewRectOut {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Fills a rectangle with the world view a camera's next render will draw.
 *
 * @param camera The camera, scrolled and zoomed for the frame.
 * @param out The rectangle to fill.
 * @returns `out`.
 * @example
 * viewAfterScroll({ scrollX: 480, scrollY: 0, width: 960, height: 540, zoomX: 2, zoomY: 2, useBounds: false, clampX, clampY }, out);
 * // { left: 720, top: 135, width: 480, height: 270 }
 */
export function viewAfterScroll(camera: ScrolledCamera, out: ViewRectOut): ViewRectOut {
  const scrollX = camera.useBounds ? camera.clampX(camera.scrollX) : camera.scrollX;
  const scrollY = camera.useBounds ? camera.clampY(camera.scrollY) : camera.scrollY;
  out.width = camera.width / camera.zoomX;
  out.height = camera.height / camera.zoomY;
  out.left = scrollX + camera.width / 2 - out.width / 2;
  out.top = scrollY + camera.height / 2 - out.height / 2;
  return out;
}
